import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card, StatTile, Modal, Avatar, Loading, inputCls, labelCls, btnPrimary } from "@/components/kit";
import {
  Barbell, SignOut, Plus, Buildings, MagnifyingGlass, ArrowRight, Key, Power, Trash, PencilSimple, UploadSimple, LockKey, Copy, CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SuperAdmin() {
  const { user, logout, enterSchool } = useAuth();
  const navigate = useNavigate();
  const [schools, setSchools] = useState(null);
  const [overview, setOverview] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", ad_name: "", ad_email: "", ad_password: "" });
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", primary_color: "#FF3B30", accent_color: "#0A84FF" });
  const [resetResult, setResetResult] = useState(null);
  const [resetCopied, setResetCopied] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [changingPw, setChangingPw] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const resetUserPw = async (u) => {
    if (!window.confirm(`Reset ${u.name}'s password? A temporary password will be generated and they'll be required to change it on next login.`)) return;
    try {
      const r = await api.post(`/users/${u.id}/reset-password`, {});
      setResetResult(r.data); setResetCopied(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reset password"); }
  };
  const copyTempPw = () => { navigator.clipboard.writeText(resetResult.temp_password); setResetCopied(true); toast.success("Copied"); setTimeout(() => setResetCopied(false), 2000); };

  const changeMyPassword = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords don't match"); return; }
    setChangingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated");
      setPwOpen(false); setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not update password"); }
    finally { setChangingPw(false); }
  };

  const openEdit = (s) => {
    setEditing(s);
    setEditForm({ name: s.name, primary_color: s.primary_color || "#FF3B30", accent_color: s.accent_color || "#0A84FF" });
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    try { await api.put(`/schools/${editing.id}`, editForm); toast.success("School updated"); setEditing(null); load(); }
    catch { toast.error("Could not update school"); }
  };
  const uploadLogo = async (file) => {
    if (!file || !editing) return;
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post(`/schools/${editing.id}/logo`, fd); setEditing(r.data); toast.success("Logo updated"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Logo upload failed"); }
  };

  const load = async () => {
    const [s, o] = await Promise.all([api.get("/schools"), api.get("/superadmin/overview")]);
    setSchools(s.data); setOverview(o.data);
  };
  useEffect(() => { load(); }, []);

  const createSchool = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/schools", form);
      toast.success("School created with its AD account");
      setShowCreate(false);
      setForm({ name: "", ad_name: "", ad_email: "", ad_password: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create school"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (s) => {
    try { await api.put(`/schools/${s.id}`, { active: !s.active }); load(); }
    catch { toast.error("Could not update school"); }
  };

  const resetAD = async (s) => {
    const pw = window.prompt(`Set a new temporary password for ${s.name}'s Athletic Director (${s.ad_email}). They'll be forced to change it on next login.`);
    if (!pw) return;
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try { await api.post(`/schools/${s.id}/reset-ad`, { password: pw }); toast.success("AD password reset"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not reset AD"); }
  };

  const deleteSchool = async (s) => {
    if (!window.confirm(`Permanently DELETE "${s.name}" and ALL its users, teams and data? This cannot be undone.`)) return;
    try { await api.delete(`/schools/${s.id}`); toast.success("School deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not delete school"); }
  };

  const enter = async (s) => {
    if (!s.active) { toast.error("Reactivate the school before managing it"); return; }
    await enterSchool({ id: s.id, name: s.name });
    navigate("/app");
  };

  const search = async (e) => {
    e.preventDefault();
    if (q.trim().length < 2) { setResults(null); return; }
    const r = await api.get(`/superadmin/search`, { params: { q } });
    setResults(r.data);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="superadmin-page">
      <header className="flex items-center justify-between px-6 md:px-10 h-20 border-b border-white/10 sticky top-0 z-30 bg-black/70 backdrop-blur-xl">
        <div className="flex items-center gap-2">
          <Barbell size={28} weight="fill" color="#FF3B30" />
          <span className="font-head text-2xl font-extrabold uppercase tracking-tight">Athlete<span className="text-[#FF3B30]">Hub</span></span>
          <span className="ml-3 text-[10px] uppercase tracking-[0.2em] font-bold text-[#0A84FF] border border-[#0A84FF]/40 rounded-sm px-2 py-1">Super Admin</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden sm:block text-sm text-[#A3A3A3]">{user?.name}</span>
          <button onClick={() => setPwOpen(true)} data-testid="superadmin-change-password"
            className="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#A3A3A3] hover:text-white transition-colors">
            <LockKey size={18} weight="bold" /> <span className="hidden sm:inline">Password</span>
          </button>
          <button onClick={() => { logout(); navigate("/login"); }} data-testid="superadmin-logout"
            className="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-[#A3A3A3] hover:text-white transition-colors">
            <SignOut size={18} weight="bold" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-[#0A84FF] text-xs uppercase tracking-[0.25em] font-bold mb-2">Platform Control</div>
            <h1 className="font-head text-4xl md:text-5xl font-black uppercase tracking-tighter leading-none">Schools</h1>
          </div>
          <button onClick={() => setShowCreate(true)} className={btnPrimary} data-testid="add-school-button">
            <Plus size={18} weight="bold" /> New School
          </button>
        </div>

        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
            <StatTile label="Schools" value={overview.schools} color="#0A84FF" testid="ov-schools" />
            <StatTile label="Active" value={overview.active_schools} color="#34C759" testid="ov-active" />
            <StatTile label="Total Users" value={overview.users} testid="ov-users" />
            <StatTile label="Total Teams" value={overview.teams} color="#FF9F0A" testid="ov-teams" />
          </div>
        )}

        <form onSubmit={search} className="flex gap-2 mb-8">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users or teams across all schools…"
              data-testid="global-search-input" className={`${inputCls} pl-10`} />
          </div>
          <button type="submit" className={btnPrimary} data-testid="global-search-button">Search</button>
        </form>

        {results && (
          <Card className="p-5 mb-8" data-testid="search-results">
            <div className="text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-3">Results</div>
            {results.users.length === 0 && results.teams.length === 0 && <div className="text-sm text-[#737373]">No matches</div>}
            {results.users.map((u) => (
              <div key={u.id} className="flex items-center gap-3 py-1.5 text-sm">
                <Avatar name={u.name} size={28} />
                <span className="text-white">{u.name}</span>
                <span className="text-[#737373]">{u.email}</span>
                <span className="ml-auto text-xs text-[#0A84FF] uppercase tracking-widest">{u.role} · {u.school_name}</span>
                {u.role !== "super_admin" && (
                  <button onClick={() => resetUserPw(u)} data-testid={`reset-user-pw-${u.id}`}
                    className="flex items-center gap-1 border border-white/20 hover:bg-white/5 text-white font-semibold uppercase tracking-widest text-[10px] px-2 py-1 rounded-sm transition-colors">
                    <Key size={12} weight="bold" /> Reset PW
                  </button>
                )}
              </div>
            ))}
            {results.teams.map((t) => (
              <div key={t.id} className="flex items-center gap-3 py-1.5 text-sm">
                <Barbell size={20} weight="fill" color="#FF3B30" />
                <span className="text-white">{t.name}</span>
                <span className="text-[#737373]">{t.sport}</span>
                <span className="ml-auto text-xs text-[#0A84FF] uppercase tracking-widest">{t.school_name}</span>
              </div>
            ))}
          </Card>
        )}

        {schools === null ? <Loading /> : schools.length === 0 ? (
          <Card className="p-12 text-center" data-testid="no-schools">
            <Buildings size={40} weight="duotone" color="#737373" className="mx-auto mb-3" />
            <div className="text-[#A3A3A3]">No schools yet. Create your first school to get started.</div>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {schools.map((s) => (
              <Card key={s.id} className="p-5" data-testid={`school-card-${s.id}`}>
                <div className="flex items-start gap-3 mb-4">
                  {s.logo_url ? (
                    <img src={`${BACKEND}${s.logo_url}`} alt={s.name} className="w-12 h-12 rounded-sm object-contain bg-white/5 border border-white/10 shrink-0" data-testid={`school-logo-${s.id}`} />
                  ) : (
                    <div className="w-12 h-12 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${s.primary_color}22`, border: `1px solid ${s.primary_color}55` }}>
                      <Buildings size={22} weight="duotone" style={{ color: s.primary_color }} />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-head text-2xl font-bold uppercase tracking-tight truncate">{s.name}</h3>
                      {!s.active && <span className="text-[10px] uppercase tracking-widest font-bold text-[#FF3B30] border border-[#FF3B30]/40 rounded-sm px-1.5 py-0.5">Inactive</span>}
                    </div>
                    <div className="text-xs text-[#737373] mt-1">AD: {s.ad_email}</div>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <span className="w-3 h-3 rounded-full border border-white/20" style={{ background: s.primary_color }} title="Primary" />
                      <span className="w-3 h-3 rounded-full border border-white/20" style={{ background: s.accent_color }} title="Accent" />
                    </div>
                  </div>
                  <button onClick={() => openEdit(s)} data-testid={`edit-school-${s.id}`}
                    className="flex items-center gap-1 border border-white/20 hover:bg-white/5 text-white font-semibold uppercase tracking-widest text-xs px-3 py-2 rounded-sm transition-colors shrink-0">
                    <PencilSimple size={14} weight="bold" /> Edit
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                  <div><div className="text-xl font-bold">{s.stats.athletes}</div><div className="text-[10px] uppercase tracking-widest text-[#737373]">Athletes</div></div>
                  <div><div className="text-xl font-bold">{s.stats.teams}</div><div className="text-[10px] uppercase tracking-widest text-[#737373]">Teams</div></div>
                  <div><div className="text-xl font-bold">{s.stats.users}</div><div className="text-[10px] uppercase tracking-widest text-[#737373]">Users</div></div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={() => enter(s)} data-testid={`enter-school-${s.id}`}
                    className="flex items-center gap-1.5 bg-[#0A84FF] hover:bg-[#3a9bff] text-white font-bold uppercase tracking-widest text-xs px-4 py-2.5 rounded-sm transition-colors">
                    Enter <ArrowRight size={14} weight="bold" />
                  </button>
                  <button onClick={() => resetAD(s)} data-testid={`reset-ad-${s.id}`}
                    className="flex items-center gap-1.5 border border-white/20 hover:bg-white/5 text-white font-semibold uppercase tracking-widest text-xs px-3 py-2.5 rounded-sm transition-colors">
                    <Key size={14} weight="bold" /> Reset AD
                  </button>
                  <button onClick={() => toggleActive(s)} data-testid={`toggle-school-${s.id}`}
                    className="flex items-center gap-1.5 border border-white/20 hover:bg-white/5 text-white font-semibold uppercase tracking-widest text-xs px-3 py-2.5 rounded-sm transition-colors">
                    <Power size={14} weight="bold" /> {s.active ? "Deactivate" : "Activate"}
                  </button>
                  <button onClick={() => deleteSchool(s)} data-testid={`delete-school-${s.id}`}
                    className="flex items-center gap-1.5 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white font-semibold uppercase tracking-widest text-xs px-3 py-2.5 rounded-sm transition-colors ml-auto">
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create School" testid="create-school-modal">
        <form onSubmit={createSchool} className="space-y-4">
          <div><label className={labelCls}>School name</label>
            <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Lincoln High" data-testid="school-name-input" /></div>
          <div><label className={labelCls}>Athletic Director name</label>
            <input className={inputCls} value={form.ad_name} onChange={(e) => setForm({ ...form, ad_name: e.target.value })} required placeholder="Jane Smith" data-testid="ad-name-input" /></div>
          <div><label className={labelCls}>AD email</label>
            <input type="email" className={inputCls} value={form.ad_email} onChange={(e) => setForm({ ...form, ad_email: e.target.value })} required placeholder="ad@school.edu" data-testid="ad-email-input" /></div>
          <div><label className={labelCls}>AD temporary password</label>
            <input className={inputCls} value={form.ad_password} onChange={(e) => setForm({ ...form, ad_password: e.target.value })} required placeholder="At least 6 characters" data-testid="ad-password-input" /></div>
          <p className="text-xs text-[#737373]">The AD will be required to change this password on first login. Avoid special characters like ! for the temporary password.</p>
          <button type="submit" disabled={saving || !form.name} className={`${btnPrimary} w-full`} data-testid="save-school-button">{saving ? "Creating…" : "Create School & AD"}</button>
        </form>
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit School" testid="edit-school-modal">
        {editing && (
          <div className="space-y-5">
            <div className="flex items-center gap-4">
              {editing.logo_url ? (
                <img src={`${BACKEND}${editing.logo_url}`} alt="logo" className="w-16 h-16 rounded-sm object-contain bg-white/5 border border-white/10" data-testid="edit-logo-preview" />
              ) : (
                <div className="w-16 h-16 rounded-sm bg-white/5 border border-white/10 flex items-center justify-center"><Buildings size={26} weight="duotone" color="#737373" /></div>
              )}
              <label className="flex items-center gap-2 cursor-pointer border border-white/20 hover:bg-white/5 text-white font-semibold uppercase tracking-widest text-xs px-4 py-2.5 rounded-sm transition-colors" data-testid="logo-upload-label">
                <UploadSimple size={16} weight="bold" /> Upload logo
                <input type="file" accept="image/*" className="hidden" data-testid="logo-file-input"
                  onChange={(e) => uploadLogo(e.target.files?.[0])} />
              </label>
            </div>
            <form onSubmit={saveEdit} className="space-y-4">
              <div><label className={labelCls}>School name</label>
                <input className={inputCls} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required data-testid="edit-name-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className={labelCls}>Primary color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={editForm.primary_color} onChange={(e) => setEditForm({ ...editForm, primary_color: e.target.value })} className="w-12 h-11 rounded-sm bg-transparent border border-white/20 cursor-pointer" data-testid="edit-primary-color" />
                    <input className={inputCls} value={editForm.primary_color} onChange={(e) => setEditForm({ ...editForm, primary_color: e.target.value })} />
                  </div></div>
                <div><label className={labelCls}>Accent color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={editForm.accent_color} onChange={(e) => setEditForm({ ...editForm, accent_color: e.target.value })} className="w-12 h-11 rounded-sm bg-transparent border border-white/20 cursor-pointer" data-testid="edit-accent-color" />
                    <input className={inputCls} value={editForm.accent_color} onChange={(e) => setEditForm({ ...editForm, accent_color: e.target.value })} />
                  </div></div>
              </div>
              <button type="submit" className={`${btnPrimary} w-full`} data-testid="save-edit-school-button">Save changes</button>
            </form>
          </div>
        )}
      </Modal>
      <Modal open={!!resetResult} onClose={() => setResetResult(null)} title="Password Reset" testid="superadmin-reset-result-modal">
        {resetResult && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-[#34C759] text-sm font-semibold"><CheckCircle size={18} weight="fill" /> Temporary password created for {resetResult.name}.</div>
            <p className="text-sm text-[#A3A3A3]">Share this with <span className="text-white font-semibold">{resetResult.email}</span>. They'll be required to set a new password on next login.</p>
            <div className="bg-[#0A0A0A] border border-white/15 rounded-sm p-3 break-all font-mono text-lg text-white text-center tracking-widest" data-testid="superadmin-reset-temp-password">{resetResult.temp_password}</div>
            <button onClick={copyTempPw} className={`${btnPrimary} w-full`} data-testid="superadmin-copy-temp-password">{resetCopied ? <><CheckCircle size={16} weight="bold" /> Copied</> : <><Copy size={16} weight="bold" /> Copy Password</>}</button>
          </div>
        )}
      </Modal>

      <Modal open={pwOpen} onClose={() => setPwOpen(false)} title="Change My Password" testid="superadmin-password-modal">
        <form onSubmit={changeMyPassword} className="space-y-4">
          <div><label className={labelCls}>Current password</label><input type="password" className={inputCls} value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} required data-testid="sa-current-password-input" /></div>
          <div><label className={labelCls}>New password</label><input type="password" className={inputCls} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required placeholder="At least 8 characters" data-testid="sa-new-password-input" /></div>
          <div><label className={labelCls}>Confirm new password</label><input type="password" className={inputCls} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required data-testid="sa-confirm-password-input" /></div>
          <button type="submit" disabled={changingPw} className={`${btnPrimary} w-full`} data-testid="sa-change-password-button">{changingPw ? "Updating…" : "Update password"}</button>
        </form>
      </Modal>
    </div>
  );
}
